if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        
        // Initialize permalink button state based on current variant availability
        // Use setTimeout to ensure DOM is fully rendered
        setTimeout(() => {
          this.updatePermalinkButtonState();
        }, 100);
        
        // Listen for variant changes to update permalink button state
        this.variantIdInput.addEventListener('change', this.updatePermalinkButtonState.bind(this));
        
        // Also listen for input events (in case variant changes without change event)
        this.variantIdInput.addEventListener('input', this.updatePermalinkButtonState.bind(this));
        
        // Listen for custom variant change events from the theme
        document.addEventListener('variant:change', this.updatePermalinkButtonState.bind(this));
        
        // Listen for quantity change events from product-info
        document.addEventListener('quantity:change', this.handleQuantityChange.bind(this));
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              // Disable permalink button when variant is sold out
              this.togglePermalinkButton(true);
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
              // Re-enable permalink button on successful add to cart
              this.updatePermalinkButtonState();
            }
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
        
        // Update permalink button state when submit button state changes
        this.updatePermalinkButtonState();
      }

      togglePermalinkButton(disable = true) {
        const permalinkButton = this.querySelector('.product-form__permalink');
        if (!permalinkButton) return;

        permalinkButton.classList.toggle('disabled', disable);
      }

      updatePermalinkButtonState() {
        const permalinkButton = this.querySelector('.product-form__permalink');
        if (!permalinkButton) return;

        // Check if variant input exists and is not disabled
        const variantInput = this.variantIdInput;
        const hasVariantValue = variantInput && variantInput.value;
        const isVariantInputDisabled = variantInput && variantInput.disabled;
        
        // Check if submit button is disabled (indicates variant is unavailable)
        const submitButton = this.submitButton;
        const isSubmitButtonDisabled = submitButton && (submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true');
        
        // Check if submit button has sold out text
        const submitButtonText = submitButton?.querySelector('span');
        const hasSoldOutText = submitButtonText && (
          submitButtonText.textContent.includes('Sold out') || 
          submitButtonText.textContent.includes('Unavailable')
        );
        
        // Determine if permalink should be disabled
        const shouldDisablePermalink = !hasVariantValue || isVariantInputDisabled || isSubmitButtonDisabled || hasSoldOutText;
        
        this.togglePermalinkButton(shouldDisablePermalink);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      handleQuantityChange(event) {
        const { variantId, quantity } = event.detail;
        
        // Validate input data
        if (!variantId || !quantity || quantity < 1) {
          return;
        }
        
        // Update permalink with new quantity
        this.updatePermalinkWithQuantity(variantId, quantity);
      }

      updatePermalinkWithQuantity(variantId, quantity) {
        const permalinkElement = this.querySelector('.product-form__permalink');
        if (!permalinkElement) return;

        const baseUrl = permalinkElement.dataset.baseUrl;
        const urlSuffix = permalinkElement.dataset.urlSuffix;
        const returnSuffix = permalinkElement.dataset.returnSuffix;
        const sellingPlanId = permalinkElement.dataset.sellingPlanId || '';

        if (!baseUrl || !urlSuffix || !returnSuffix) {
          return;
        }

        let newHref = `${baseUrl}${variantId}${urlSuffix}${sellingPlanId}${returnSuffix}`;
        
        if (newHref.includes('items[') && newHref.includes('][quantity]=')) {
          newHref = newHref.replace(/items\[\d+\]\[quantity\]=\d+/g, `items[0][quantity]=${quantity}`);
        }
        else if (newHref.includes('quantity=')) {
          newHref = newHref.replace(/quantity=\d+/g, `quantity=${quantity}`);
        }
        
        permalinkElement.href = newHref;
      }

      // Public method to force update permalink button state
      forceUpdatePermalinkState() {
        this.updatePermalinkButtonState();
      }
    }
  );
}
